import {
  WSAvatar,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSLoader,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { WSQueries } from "../../../../query/WSQuery";
import { queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import {
  avatarBankAccount,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { useTaxWithholdingsStatus } from "../../../TaxWithholdings/hooks/useTaxWithholdingsStatus";
import { PATH_TAX_WITHHOLDINGS } from "../../../TaxWithholdings/paths";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD } from "../../paths";
import { payoutDestinationTypeToMethodType } from "../payout/DefaultPayoutMethod";
import { SplitMethod } from "./SplitMethod";

type Props = {
  onEditWallet?: () => void;
} & WSElementProps;

export const PayoutSplits: React.FC<Props> = ({
  onEditWallet,
  ...elementProps
}) => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const history = useHistory();
  const location = useLocation();

  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const bankingAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );

  const { isActive, turnOn, turnOff, updateMeta, taxWithholdingAccount } =
    useTaxWithholdingsStatus();

  return (
    <WSElement {...elementProps}>
      <WSText.Paragraph weight="medium" mb="XL">
        Payout splits
      </WSText.Paragraph>
      <WSElement>
        <WSQueries
          queries={{
            queryPayoutSettings
          }}
        >
          {({ queryPayoutSettings: { data: payoutSettings } }) => {
            const destination = payoutSettings
              ? selectorPayoutSettingsDefaultDestination(payoutSettings)
              : undefined;

            const walletItem = bankingAccount ? (
              <Item
                item={
                  <SplitMethod
                    type="InternalAccount"
                    id={bankingAccount.id}
                    hideNumbers
                  />
                }
                onEdit={() => {
                  onEditWallet
                    ? onEditWallet()
                    : history.push("/member/wallet");
                }}
                value={`${destination ? 100 - (destination.percentage ?? 0) : 100}%`}
              />
            ) : (
              <Item
                item={
                  <>
                    <WSAvatar size="S" {...avatarWallet} />
                    <WSText.ParagraphSm color="gray500">
                      Wingspan Wallet
                    </WSText.ParagraphSm>
                  </>
                }
                disabled
                onAdd={() => {
                  history.push("/member/wallet");
                }}
              />
            );

            const payoutMethodItem =
              destination && destination.id ? (
                <Item
                  item={
                    <SplitMethod
                      type={payoutDestinationTypeToMethodType(destination.type)}
                      id={destination.id}
                    />
                  }
                  value={`${destination.percentage ?? 0}%`}
                />
              ) : bankingAccount ? (
                <Item
                  item={
                    <>
                      <WSAvatar size="S" {...avatarBankAccount} />
                      <WSText.ParagraphSm color="gray500">
                        No split method
                      </WSText.ParagraphSm>
                    </>
                  }
                  disabled
                  value="0%"
                />
              ) : (
                <Item
                  item={
                    <>
                      <WSAvatar size="S" {...avatarBankAccount} />
                      <WSText.ParagraphSm color="gray500">
                        Add payout method
                      </WSText.ParagraphSm>
                    </>
                  }
                  disabled
                  onAdd={() => {
                    history.push(
                      PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD
                    );
                  }}
                />
              );

            const texWithholdingItem = taxWithholdingAccount ? (
              <Item
                item={
                  <SplitMethod
                    type="InternalAccount"
                    id={taxWithholdingAccount.id}
                    hideNumbers
                  />
                }
                onEdit={() => {
                  if (location.pathname.includes(PATH_TAX_WITHHOLDINGS)) {
                    isActive ? turnOff() : turnOn();
                  } else {
                    history.push(PATH_TAX_WITHHOLDINGS);
                  }
                }}
                value={
                  updateMeta.isLoading ? (
                    <WSLoader.Spinner size="XS" />
                  ) : isActive ? (
                    "On"
                  ) : (
                    "Off"
                  )
                }
              />
            ) : (
              <Item
                item={
                  <>
                    <WSAvatar size="S" {...avatarWallet} />
                    <WSText.ParagraphSm color="gray500">
                      Tax Withholding
                    </WSText.ParagraphSm>
                  </>
                }
                disabled
                onAdd={() => {
                  history.push(PATH_TAX_WITHHOLDINGS);
                }}
              />
            );

            const items: ReactNode[] = [];
            if (!destination && !!bankingAccount) {
              items.push(walletItem);
              items.push(payoutMethodItem);
            } else {
              items.push(payoutMethodItem);
              items.push(walletItem);
            }
            items.push(texWithholdingItem);

            return items.map((item, index) => (
              <WSElement
                key={String(index)}
                mb={index === items.length - 1 ? undefined : "M"}
              >
                {item}
              </WSElement>
            ));
          }}
        </WSQueries>
      </WSElement>
    </WSElement>
  );
};

type ItemProps = {
  item: ReactNode;
  disabled?: boolean;
  pending?: boolean;
  value?: ReactNode;
  onAdd?: () => void;
  onEdit?: () => void;
} & WSElementProps;

const Item: React.FC<ItemProps> = ({
  item,
  value,
  disabled,
  pending,
  onAdd,
  onEdit
}) => (
  <WSFlexBox justify="space-between" wrap="nowrap" alignItems="center">
    <WSFlexBox
      wrap="nowrap"
      alignItems="center"
      style={{ opacity: disabled ? 0.6 : undefined }}
      gap="S"
    >
      {item}
    </WSFlexBox>
    <WSFlexBox wrap="nowrap" alignItems="center">
      {onAdd && (
        <WSButton.Link size="M" icon="plus" onClick={onAdd}>
          Add
        </WSButton.Link>
      )}
      {onEdit && (
        <WSIcon block mr="M" name="edit" color="gray500" onClick={onEdit} />
      )}
      {value && <WSText.ParagraphSm>{value}</WSText.ParagraphSm>}
      {pending && <WSPill icon="time" text="Pending" />}
    </WSFlexBox>
  </WSFlexBox>
);
